import { Button, Icon, SettingsGroup, SettingsItemCheckbox, SettingsItemTextField } from '@elipssolution/harfang';
import { mdiPencil } from '@mdi/js';
import { Stack } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import ExternalApplicationFormField from './ExternalApplicationFormField';
import { useSettingsDialog } from '../../../../hooks/useSettingsDialog';
import { WidgetExternalApplicationType } from '../../../../types/widget';
import { DefaultFormType, FormFieldEnum, SpecificFieldsType } from '../types/form';

type ExternalApplicationFormProps = {
	application: WidgetExternalApplicationType;
	isNew: boolean;
	specificFields?: SpecificFieldsType[];
	onSubmit: (application: WidgetExternalApplicationType) => void;
	onRemove: (application: WidgetExternalApplicationType) => void;
};

const ExternalApplicationForm = ({
	application,
	isNew,
	specificFields,
	onSubmit,
	onRemove,
}: ExternalApplicationFormProps) => {
	const {
		key,
		type,
		name: initialName,
		isInternal: isInternalInitial,
		isExternal: isExternalInitial,
		isAvailableOnDesktopFormat: isAvailableOnDesktopFormatInitial,
		isAvailableOnMobileFormat: isAvailableOnMobileFormatInitial,
	} = application;

	const { onHasDirtyFormChange, onCloseConfirmationTitleChange } = useSettingsDialog();

	const {
		control,
		formState: { isValid, isDirty },
		handleSubmit,
		setValue,
		watch,
	} = useForm<DefaultFormType>({
		defaultValues: {
			name: initialName,
			isInternal: isInternalInitial,
			isExternal: isExternalInitial,
			isAvailableOnDesktopFormat: isAvailableOnDesktopFormatInitial,
			isAvailableOnMobileFormat: isAvailableOnMobileFormatInitial,
			...specificFields?.reduce((acc, specificField) => {
				if (specificField.type !== FormFieldEnum.IMAGE) {
					const { name, value } = specificField || {};

					acc[name] = value as
						| string
						| boolean
						| Record<string, string | boolean | { [key: string]: unknown } | undefined>
						| undefined;
				}

				return acc;
			}, {} as Record<string, string | boolean | { [key: string]: unknown } | undefined>),
		},
	});

	const handleRemove = () => onRemove(application);

	const handleSubmitForm = ({
		name,
		isInternal,
		isExternal,
		isAvailableOnDesktopFormat,
		isAvailableOnMobileFormat,
		...specific
	}: DefaultFormType) =>
		onSubmit({
			key,
			type,
			name,
			isInternal,
			isExternal,
			isAvailableOnDesktopFormat,
			isAvailableOnMobileFormat,
			...(!!specific && { specific: JSON.stringify({ ...specific }) }),
		});

	useEffect(() => {
		if (
			specificFields?.some(({ type: specificFieldType, value }) => specificFieldType === FormFieldEnum.IMAGE && !!value)
		) {
			specificFields.forEach(({ type: specificFieldType, value, name }) => {
				const specificFieldControllerValue = watch(name);

				if (!specificFieldControllerValue && specificFieldType === FormFieldEnum.IMAGE && !!value) {
					setValue(name, value);
				}
			});
		}
	}, [setValue, specificFields, watch]);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
		onCloseConfirmationTitleChange(
			`Êtes-vous sûr de vouloir quitter la ${isNew ? 'création' : 'modification'} de l'application externe ?`,
		);
	}, [isDirty, onCloseConfirmationTitleChange, onHasDirtyFormChange, application, initialName, isNew]);

	return (
		<>
			<SettingsGroup label="Configuration">
				<Controller
					name="name"
					control={control}
					render={({ field }) => (
						<SettingsItemTextField {...field} description="Nom de l'application." label="Nom" required />
					)}
					rules={{ required: true }}
				/>

				{!!specificFields && specificFields.length > 0 && (
					<ExternalApplicationFormField control={control} specificFields={specificFields} />
				)}

				<Controller
					name="isInternal"
					control={control}
					defaultValue={false}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, les utilisateurs cabinet auront accès à cette application."
							label="Visible cabinet"
						/>
					)}
				/>
				<Controller
					name="isExternal"
					control={control}
					defaultValue={false}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, les utilisateurs entreprise auront accès à cette application."
							label="Visible entreprise"
						/>
					)}
				/>
				<Controller
					defaultValue={false}
					name="isAvailableOnDesktopFormat"
					control={control}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, l'application externe sera visible sur ordinateur."
							label="Visible version ordinateur"
						/>
					)}
				/>
				<Controller
					defaultValue={false}
					name="isAvailableOnMobileFormat"
					control={control}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, l'application externe sera visible sur mobile."
							label="Visible version mobile"
						/>
					)}
				/>
			</SettingsGroup>

			<Stack direction="row-reverse" justifyContent="space-between" gap={1}>
				<Button
					disabled={!isValid}
					startIcon={<Icon path={mdiPencil} size="small" />}
					variant="contained"
					onClick={handleSubmit(handleSubmitForm)}
				>
					{isNew ? 'Ajouter' : 'Modifier'}
				</Button>

				{!isNew && (
					<Button variant="outlined" color="error" onClick={handleRemove}>
						Supprimer
					</Button>
				)}
			</Stack>
		</>
	);
};

export default ExternalApplicationForm;
